// =============================================================
// CardCarousel
// Stack-style swipeable card carousel for the intruder hand.
//
// Design goals (per product spec):
//   - Smooth stack animation: the next card slides forward and
//     the active one falls back into the stack.
//   - Horizontal swipes must NOT trigger browser back-navigation.
//     The intruder page never scrolls, so the gesture surface uses
//     `touch-action: none`: the browser never claims the touch for
//     a pan (which would cancel our pointer stream mid-swipe) and we
//     handle all motion ourselves.
//   - The deck follows the finger 1:1, and a short swipe (~15% width)
//     or a quick flick is enough to change cards.
//   - Cards are coloured per action type (EMP=cyan, Fake Task=pink,
//     Remote Sabotage=red, etc.) so the player gets a quick read
//     on what's in their hand.
//   - Tapping the middle card plays it. Cards that open a sheet
//     (Fake Task, Taunt) open it straight away, and the sheet has a
//     Cancel. Every other card counts down for UNDO_MS first, and a
//     second tap cancels it, so a stray tap can be taken back.
//     Tapping a peeked side card just brings it forward.
//   - `compact` mode trims the deck height when the page also has
//     to show Active Effects above; otherwise the deck stretches
//     to feel like a real hand of cards.
// =============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cardCopy } from '../utils/cardCopy';
import {
    Zap, Clock, MapPin, Hand,
    Radiation, ClipboardX, Megaphone, Volume2, Ban, RefreshCw, FastForward,
    Skull, MessageSquare,
} from '../signal/icons';
import { t } from '../i18n';

const SWIPE_THRESHOLD_RATIO = 0.15; // fraction of width before commit
const FLICK_VELOCITY = 0.3;          // px/ms over the last moments of the drag
const FLICK_MIN_PX = 24;             // a flick still has to travel this far
const FLICK_WINDOW_MS = 100;         // velocity is measured over this window
const LOCK_SLOP_PX = 8;              // movement before we decide swipe vs press
const CARD_WIDTH_RATIO = 0.76;       // matches w-[76%] max-w-[320px] below
const CARD_MAX_WIDTH = 320;
const SLOT_SHIFT_RATIO = 0.78;       // each slot is 78% of a card width apart
const TRANSITION_MS = 320;
const MAX_VISIBLE_PEEK = 2;          // cards visible on each side
const UNDO_MS = 2000;                 // a tapped card waits this long before it plays

// ----- Action -> colour -----
// Keys match the `action` strings used in server/assets/card.py. Each card is one solid
// Signal colour so the hand reads at a glance: what a card does is its colour and its icon.
const SG = { blue: '#1b4fd8', yellow: '#ffc21a', red: '#e0263a', hazard: '#ff7a1a', ink: '#1a1400', white: '#ffffff' };
const CARD_THEMES = {
    'EMP':              { bg: SG.blue,   fg: SG.white },
    'Fake Task':        { bg: SG.yellow, fg: SG.ink },
    'Remote Sabotage':  { bg: SG.hazard, fg: SG.ink },
    'Self Report':      { bg: SG.white,  fg: SG.ink },
    'Taunt':            { bg: SG.red,    fg: SG.white },
    'Taunt Message':    { bg: SG.red,    fg: SG.white },
    'Area Denial':      { bg: SG.white,  fg: SG.red },
    'Discard and Draw': { bg: SG.yellow, fg: SG.ink },
    'Shorten Meltdown': { bg: SG.hazard, fg: SG.ink },
};
const DEFAULT_THEME = { bg: SG.red, fg: SG.white };

export function themeFor(action) {
    return CARD_THEMES[action] || DEFAULT_THEME;
}

// Per-action large illustration shown in the empty area below the
// description. Helps players recognize a card at a glance and gives
// the body of the card visual weight on smaller screens where there
// would otherwise be a lot of dead space.
const ACTION_ICONS = {
    'EMP': Zap,
    'Fake Task': ClipboardX,
    'Remote Sabotage': Radiation,
    'Self Report': Megaphone,
    'Taunt': Volume2,
    'Taunt Message': MessageSquare,
    'Area Denial': Ban,
    'Discard and Draw': RefreshCw,
    'Shorten Meltdown': FastForward,
};
function iconFor(action) {
    return ACTION_ICONS[action] || Skull;
}

function CarouselCard({
    card,
    offset,
    isActive,
    isAnimating,
    compact,
    playProgress,
    onTap,
}) {
    const absOffset = Math.abs(offset);

    const translatePct = offset * SLOT_SHIFT_RATIO * 100;
    const scale = Math.max(0.82, 1 - absOffset * 0.08);
    const rotate = offset * 4;
    const opacity = Math.max(0, 1 - absOffset * 0.35);
    const zIndex = 10 - Math.round(absOffset * 2);

    const style = {
        transform: `translate(-50%, -50%) translateX(${translatePct}%) scale(${scale}) rotate(${rotate}deg)`,
        opacity,
        zIndex,
        transition: isAnimating ? `transform ${TRANSITION_MS}ms cubic-bezier(.22,.9,.32,1.2), opacity ${TRANSITION_MS}ms ease` : 'none',
        pointerEvents: absOffset > MAX_VISIBLE_PEEK ? 'none' : 'auto',
    };

    const { action, location, duration } = card;
    const copy = cardCopy(card);
    const theme = themeFor(action);
    const ActionIcon = iconFor(action);

    return (
        <div
            className={`absolute top-1/2 left-1/2 w-[76%] max-w-[320px] h-[94%] max-h-[560px] select-none cursor-pointer flex flex-col sg-card ${compact ? 'sg-card--compact' : ''}`}
            style={{ ...style, background: theme.bg, color: theme.fg }}
            onClick={(e) => { e.stopPropagation(); onTap && onTap(); }}
            aria-hidden={!isActive}
        >
            <h3 className="sg-card__title">{copy.name}</h3>
            <p className="sg-card__text">{copy.does}</p>
            {copy.when && <p className="sg-card__text sg-card__when">{copy.when}</p>}

            {/* The action's icon fills whatever room is left */}
            <div className="flex-1 min-h-0 flex items-center justify-center">
                <ActionIcon
                    strokeWidth={2.5}
                    style={{ width: '62%', height: '100%', maxHeight: compact ? 90 : 170, maxWidth: 170 }}
                    aria-hidden="true"
                />
            </div>

            <div className="sg-card__chips">
                {location && <span><MapPin size={14} /> {location}</span>}
                {duration && <span><Clock size={14} /> {duration}s</span>}
            </div>

            {/* Tapped: the card fills from the bottom until it plays */}
            {isActive && playProgress > 0 && (
                <>
                    <div className="sg-card__fill" style={{ height: `${Math.min(100, playProgress * 100)}%` }} />
                    <div className="sg-card__pending" role="status" aria-live="polite">
                        {t('Playing… tap to cancel')}
                    </div>
                </>
            )}
        </div>
    );
}

export default function CardCarousel({ cards, onPlayCard, compact = false, onActiveIndexChange }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const [dragX, setDragX] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [playProgress, setPlayProgress] = useState(0);
    const containerRef = useRef(null);
    const widthRef = useRef(0);
    const dragState = useRef(null);
    // The card counting down to play: { id, start, raf }.
    const pendingRef = useRef(null);
    const activeCardRef = useRef(null);
    // The latest drag distance, kept outside React state so pointerup
    // never decides on a value from a render that hasn't happened yet.
    const dragXRef = useRef(0);
    const animTimerRef = useRef(null);
    const suppressClickRef = useRef(false);

    useEffect(() => () => {
        if (pendingRef.current) cancelAnimationFrame(pendingRef.current.raf);
        if (animTimerRef.current) clearTimeout(animTimerRef.current);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;
        const measure = () => {
            widthRef.current = containerRef.current?.offsetWidth || 0;
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (activeIndex >= cards.length && cards.length > 0) {
            setActiveIndex(cards.length - 1);
        }
    }, [cards.length, activeIndex]);

    useEffect(() => {
        if (!onActiveIndexChange || cards.length === 0) return;
        onActiveIndexChange(activeIndex, cards[activeIndex]);
    }, [activeIndex, cards, onActiveIndexChange]);

    const updateDragX = useCallback((x) => {
        dragXRef.current = x;
        setDragX(x);
    }, []);

    // Ease the deck to `newIndex` (or back to where it was).
    const settleTo = useCallback((newIndex) => {
        setIsAnimating(true);
        updateDragX(0);
        if (newIndex !== undefined) setActiveIndex(newIndex);
        if (animTimerRef.current) clearTimeout(animTimerRef.current);
        animTimerRef.current = setTimeout(() => setIsAnimating(false), TRANSITION_MS + 20);
    }, [updateDragX]);

    const cancelPending = useCallback(() => {
        if (pendingRef.current) cancelAnimationFrame(pendingRef.current.raf);
        pendingRef.current = null;
        setPlayProgress(0);
    }, []);

    const isOnActiveCard = useCallback((clientX) => {
        const c = containerRef.current;
        if (!c) return false;
        const rect = c.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cardW = Math.min(rect.width * CARD_WIDTH_RATIO, CARD_MAX_WIDTH);
        return Math.abs(clientX - cx) <= cardW / 2;
    }, []);

    // Tap the middle card: a sheet card opens its sheet now; any other card
    // counts down and plays unless it is tapped again.
    const tapActiveCard = useCallback(() => {
        const card = activeCardRef.current;
        if (!card) return;
        if (pendingRef.current) {
            cancelPending();
            return;
        }
        if (card.requires_input) {
            onPlayCard(card);
            return;
        }
        const pending = { id: card.id, start: performance.now(), raf: null };
        const tick = () => {
            if (pendingRef.current !== pending) return;
            const p = Math.min(1, (performance.now() - pending.start) / UNDO_MS);
            setPlayProgress(p);
            if (p >= 1) {
                pendingRef.current = null;
                setPlayProgress(0);
                onPlayCard(card);
                return;
            }
            pending.raf = requestAnimationFrame(tick);
        };
        pendingRef.current = pending;
        setPlayProgress(0.001);
        pending.raf = requestAnimationFrame(tick);
    }, [cancelPending, onPlayCard]);

    // A card that left the hand, or a move to another card, drops the countdown.
    useEffect(() => {
        const pending = pendingRef.current;
        if (pending && cards[activeIndex]?.id !== pending.id) cancelPending();
    }, [cards, activeIndex, cancelPending]);

    const handlePointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return;
        const onActive = isOnActiveCard(e.clientX);
        const now = performance.now();
        suppressClickRef.current = false;
        dragState.current = {
            startX: e.clientX,
            startY: e.clientY,
            locked: false,
            pointerId: e.pointerId,
            onActive,
            samples: [{ x: e.clientX, t: now }],
        };
        // Grabbing the deck mid-animation snaps it into place so the
        // drag tracks the finger right away.
        if (animTimerRef.current) clearTimeout(animTimerRef.current);
        setIsAnimating(false);
    }, [isOnActiveCard]);

    const handlePointerMove = useCallback((e) => {
        const s = dragState.current;
        if (!s) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        const now = performance.now();
        s.samples.push({ x: e.clientX, t: now });
        while (s.samples.length > 2 && now - s.samples[0].t > FLICK_WINDOW_MS) s.samples.shift();
        if (!s.locked) {
            if (Math.abs(dx) < LOCK_SLOP_PX && Math.abs(dy) < LOCK_SLOP_PX) return;
            // Thumbs swipe in an arc, so only a clearly vertical drag
            // is ignored.
            if (Math.abs(dy) > Math.abs(dx) * 1.5 || cards.length <= 1) {
                dragState.current = null;
                return;
            }
            s.locked = true;
            suppressClickRef.current = true;
            try { e.currentTarget.setPointerCapture(s.pointerId); } catch (_) {}
        }
        let effective = dx;
        if ((activeIndex === 0 && dx > 0) || (activeIndex === cards.length - 1 && dx < 0)) {
            effective = dx * 0.35;
        }
        updateDragX(effective);
        if (e.cancelable) e.preventDefault();
    }, [activeIndex, cards.length, updateDragX]);

    const endDrag = useCallback((e) => {
        const s = dragState.current;
        dragState.current = null;
        if (!s) return;
        if (s.locked) {
            try { e.currentTarget.releasePointerCapture(s.pointerId); } catch (_) {}

            const w = widthRef.current || 1;
            const x = dragXRef.current;
            const first = s.samples[0];
            const last = s.samples[s.samples.length - 1];
            const velocity = (last.x - first.x) / Math.max(1, last.t - first.t);

            const passedDistance = Math.abs(x) > w * SWIPE_THRESHOLD_RATIO;
            // A flick counts only when it is still moving the same way
            // it was dragged, so pulling back and letting go cancels.
            const flicked = Math.abs(velocity) > FLICK_VELOCITY
                && Math.abs(x) > FLICK_MIN_PX
                && Math.sign(velocity) === Math.sign(x);

            if ((passedDistance || flicked) && cards.length > 1) {
                const direction = x < 0 ? 1 : -1;
                const next = Math.min(cards.length - 1, Math.max(0, activeIndex + direction));
                if (next !== activeIndex) {
                    settleTo(next);
                    return;
                }
            }
            settleTo();
            return;
        }

        // Pointer released without locking into a swipe: a tap. Only a
        // pointer that came down on the middle card plays it.
        if (s.onActive && e.type === 'pointerup') tapActiveCard();
    }, [activeIndex, cards.length, settleTo, tapActiveCard]);

    if (cards.length === 0) return null;

    // Tap on a side card brings it forward. A tap on the middle card is
    // handled at the deck level (tapActiveCard), so it plays exactly once.
    const handleCardTap = (i) => {
        // A mouse drag ends with a click on whatever card is under the
        // pointer; that was a swipe, not a tap.
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
        }
        if (i === activeIndex) return;
        settleTo(i);
    };

    const activeCard = cards[activeIndex];
    activeCardRef.current = activeCard;
    const activeTheme = themeFor(activeCard?.action);

    // Move the whole stack with the finger: dragging one slot's width
    // brings the neighbouring card exactly into the middle.
    const slotWidth = Math.min(widthRef.current * CARD_WIDTH_RATIO, CARD_MAX_WIDTH) * SLOT_SHIFT_RATIO;
    const dragOffset = slotWidth > 0 ? dragX / slotWidth : 0;

    // Deck height: in non-compact mode the deck fills the flex column
    // it's nested in (page must give it a flex-1 + min-h-0 parent), so
    // it adapts to whatever screen real estate is actually free without
    // forcing the page to scroll. The min-h floor is intentionally low
    // so the card stays visible (not collapsed) on short screens like
    // the iPhone SE; max-h keeps it from getting comically tall on
    // big tablets. Compact mode is also flex-1 (with a tighter floor
    // and ceiling) so on short screens the deck still fits below the
    // active effects panel instead of being pushed off-screen.
    const deckHeight = compact
        ? 'flex-1 min-h-[180px] max-h-[420px]'
        : 'flex-1 min-h-[200px] max-h-[640px]';

    return (
        <div className="w-full h-full flex flex-col min-h-0 relative">
            {/* Hand counter — hidden in compact mode to reclaim vertical
                space on small screens. The dot pager already shows the
                same info. */}
            {!compact && (
                <div className="flex items-center justify-between mb-2 px-1" style={{ fontWeight: 800 }}>
                    <span>{t('Your cards')}</span>
                    <span className="sg-num">{activeIndex + 1} / {cards.length}</span>
                </div>
            )}

            {/* Deck — gesture surface */}
            <div
                ref={containerRef}
                data-testid="card-deck"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                className={`relative w-full ${deckHeight} overflow-hidden`}
                style={{
                    touchAction: 'none',
                    overscrollBehaviorX: 'contain',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                }}
            >
                {cards.map((card, i) => (
                    <CarouselCard
                        key={card.id || i}
                        card={card}
                        offset={i - activeIndex + dragOffset}
                        isActive={i === activeIndex}
                        isAnimating={isAnimating}
                        compact={compact}
                        playProgress={i === activeIndex ? playProgress : 0}
                        onTap={() => handleCardTap(i)}
                    />
                ))}
            </div>

            {/* Dot indicators */}
            {cards.length > 1 && (
                <div className={`flex items-center justify-center gap-1.5 ${compact ? 'mt-2' : 'mt-3'}`}>
                    {cards.map((card, i) => (
                        <button
                            key={card.id || i}
                            type="button"
                            onClick={() => settleTo(i)}
                            aria-label={t('Go to card {number}', { number: i + 1 })}
                            className={`h-2 rounded-full transition-all ${i === activeIndex ? 'w-7' : 'w-2'}`}
                            style={{ background: i === activeIndex ? activeTheme.bg : 'rgba(255,255,255,0.3)' }}
                        />
                    ))}
                </div>
            )}

            {!compact && (
                <p className="text-center mt-2 flex items-center justify-center gap-1.5" style={{ fontWeight: 700, fontSize: 14, opacity: 0.7 }}>
                    <Hand size={16} />
                    {cards.length > 1 ? t('Swipe to browse · Tap a card to play it') : t('Tap the card to play it')}
                </p>
            )}
        </div>
    );
}
